/* eslint-disable no-unused-vars */
import i18n from './i18n/i18n'; // Initialize i18n BEFORE rendering
import './init/fetchFallback';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
// Defer heavy telemetry components to speed initial load
import './index.css';
import App from './App';
import { logBootEvent } from './init/bootLogger';

const CHUNK_RELOAD_FLAG = 'chunk_reload_recovered_v1';
const ENABLE_SERVICE_WORKER = false;

async function hardResetClientCachesAndServiceWorkers() {
  if (typeof window === 'undefined') return;

  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }
  } catch (error) {
    console.debug('Service worker hard reset: unregister failed', error);
  }

  try {
    if (typeof caches !== 'undefined' && caches.keys) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
    }
  } catch (error) {
    console.debug('Service worker hard reset: cache delete failed', error);
  }
}

function isChunkLoadFailure(errorLike) {
  const text = String(
    errorLike?.message
    || errorLike?.reason?.message
    || errorLike?.toString?.()
    || ''
  ).toLowerCase();

  return text.includes('chunkloaderror')
    || text.includes('loading chunk')
    || text.includes('failed to fetch dynamically imported module');
}

function recoverFromChunkLoadFailure(errorLike) {
  if (!isChunkLoadFailure(errorLike)) return false;

  try {
    const alreadyRecovered = window.sessionStorage.getItem(CHUNK_RELOAD_FLAG);
    if (alreadyRecovered) return false;
    window.sessionStorage.setItem(CHUNK_RELOAD_FLAG, new Date().toISOString());
  } catch {
    // ignore storage access failures and still attempt reload once
  }

  window.location.reload();
  return true;
}

window.addEventListener('error', (event) => {
  // Handle chunk-load failures as before
  if (recoverFromChunkLoadFailure(event?.error || event?.message)) {
    event.preventDefault?.();
    return;
  }

  // Leaflet/React race can throw NotFoundError removeChild; log and swallow to avoid uncaught crash
  try {
    const msg = String(event?.error?.message || event?.message || '');
    if (msg.includes("Failed to execute 'removeChild' on 'Node'")) {
      console.warn('Suppressed known Leaflet/React removeChild race error:', msg);
      event.preventDefault?.();
      return;
    }
  } catch (e) {
    // ignore
  }
});

window.addEventListener('unhandledrejection', (event) => {
  if (recoverFromChunkLoadFailure(event?.reason)) {
    event.preventDefault?.();
  }
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <App />
      {/* Analytics and SpeedInsights are lazy-loaded after initial render to reduce main bundle size */}
    </BrowserRouter>
  </React.StrictMode>
);

// Mark initial route as ready once the first paint cycle completes.
try {
  setTimeout(() => logBootEvent('initial route ready'), 50);
} catch (e) {
  void e;
}

// Lazy-load non-critical telemetry components after a short delay / idle
setTimeout(async () => {
  try {
    const [{ Analytics }, { SpeedInsights }] = await Promise.all([
      import('@vercel/analytics/react'),
      import('@vercel/speed-insights/react')
    ]);

    const mount = document.createElement('div');
    mount.setAttribute('id', 'non-critical-telemetry');
    document.body.appendChild(mount);
    try {
      const telemetryRoot = ReactDOM.createRoot(mount);
      telemetryRoot.render(
        <React.StrictMode>
          <Analytics />
          <SpeedInsights />
        </React.StrictMode>
      );
    } catch (e) {
      // ignore telemetry render failures
      console.debug('Telemetry render failed', e);
    }
  } catch (e) {
    // ignore dynamic import failures
    console.debug('Lazy-load telemetry failed', e);
  }
}, 3000);

// SERVICE WORKER REGISTRATION (temporary disabled while cache reset rollout is active)
if ('serviceWorker' in navigator) {
  window.addEventListener('load', async () => {
    await hardResetClientCachesAndServiceWorkers();

    if (!ENABLE_SERVICE_WORKER) {
      console.info('Service worker kill switch active: skipped registration');
      return;
    }

    const isNativeAndroid = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';

    // In native Android WebView we prefer APK-bundled assets over SW cache.
    // This avoids stale JS/CSS after app updates.
    if (isNativeAndroid) {
      try {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map(r => r.unregister()));
        console.info('Native Android: service workers unregistered');
      } catch (e) {
        console.debug('Native Android: service worker unregister failed', e);
      }
      return;
    }

    // Migration helper: some users may have older service workers still active
    // which intercept cross-origin tile requests. To ensure they get the
    // updated worker (or none), unregister existing registrations once per
    // client. We store a flag in localStorage to avoid repeating this every
    // load.
    try {
      const flag = localStorage.getItem('sw_unregistered_v1');
      if (!flag) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map(r => r.unregister()));
        localStorage.setItem('sw_unregistered_v1', new Date().toISOString());
        console.info('Service Worker migration: unregistered existing workers');
      }
    } catch (e) {
      console.debug('Service Worker migration: unregister failed', e);
    }

    // Register the (current) service worker. The worker file has been updated
    // to avoid intercepting cross-origin resources like map tiles.
    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      // Service worker registered successfully
    } catch (error) {
      console.error('❌ Service Worker registration failed:', error);
    }
  });
}
